import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { Tooltip, TooltipShield } from '@/components/ui/tooltip';
import { setFarewell } from '@/lib/protocol-step-draft';
import { cn } from '@/lib/utils';
import type { ProtocolStepDraft } from '@matter_extractor/shared';
import { farewellEligible } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * The checkbox « Étape d'adieu » (issue #163, §7.2; maquette
 * `adieu/etape-adieu.html`, variante A).
 *
 * ## Greyed, never emptying the step for the operator
 *
 * The box ticks only on a step with no objective and no exit condition
 * (`farewellEligible`, H92) - the same function `protocolProblems` refuses a
 * farewell step with, so the box and the refusal cannot disagree. Elsewhere it
 * is DISABLED with the reason in a bubble: « rien ne se perd sans se voir, et
 * aucune variable `{{objectif:...}}` ne casse ». Clearing the step on a click,
 * after a confirmation or not, is one of the alternatives the grill set aside.
 *
 * A step ALREADY marked farewell keeps its box live whatever it carries: a
 * content posed by hand or by the assistant can hold objectives on one, and the
 * only way to see them again is to untick.
 *
 * ## The bubble hangs on the shield, not on the input
 *
 * A `disabled` control receives neither pointer nor focus, so the bubble that
 * says why hangs on `TooltipShield`, which becomes focusable exactly when the
 * control inside is closed (`components/ui/tooltip.tsx`). The sentence is in
 * the DOM only while the bubble is open, and a touch screen never opens it:
 * the greyed box then says « closed » and the step's own panels, in plain view
 * right below, say what is in the way.
 */

export interface FarewellOptionProps {
  readonly step: ProtocolStepDraft;
  readonly readOnly: boolean;
  readonly onChangeStep: (next: ProtocolStepDraft) => void;
}

/** Why the box is greyed - §7.2, word for word. */
export const FAREWELL_BLOCKED_REASON =
  'Retirez d’abord les objectifs et les conditions de cette étape.';

export function FarewellOption({ step, readOnly, onChangeStep }: FarewellOptionProps) {
  const id = useId();
  // Blocked by CONTENT, which is what the bubble explains. Read-only is not a
  // reason to give: the whole editor is read-only then, and says so above.
  const blocked = !step.farewell && !farewellEligible(step);
  const closed = readOnly || blocked;
  // Only a box closed FOR A REASON is a tab stop: the shield's focus exists to
  // open the bubble, and a read-only box has no bubble to open.
  const explained = blocked && !readOnly;

  return (
    <Tooltip label={explained ? FAREWELL_BLOCKED_REASON : undefined}>
      <TooltipShield closed={explained} className="items-start">
        <label
          htmlFor={id}
          className={cn(
            'flex items-start gap-2.5',
            closed ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <input
            id={id}
            type="checkbox"
            data-testid={PROTOCOL_TEST_IDS.stepFarewell}
            checked={step.farewell}
            disabled={closed}
            onChange={(event) => {
              // `disabled` alone is not the guard: a synthetic click still
              // reaches the handler in some environments (jsdom does), and a
              // greyed box that ticked anyway would mark a step that still
              // carries objectives as farewell. A closed box writes nothing,
              // whatever event arrives; the bubble lives on the shield.
              if (closed) return;
              onChangeStep(setFarewell(step, event.currentTarget.checked));
            }}
            className="mt-1 size-4.5 shrink-0 accent-bleu-nuit disabled:cursor-not-allowed"
          />
          <span>
            <span
              className={cn(
                'block font-semibold text-sm',
                blocked ? 'text-gray-400' : 'text-gray-800',
              )}
            >
              Étape d’adieu
            </span>
            <span className={cn('block text-caption', blocked ? 'text-gray-400' : 'text-gray-600')}>
              Un seul message, sans réponse attendue ; le protocole se clôt ensuite.
            </span>
          </span>
        </label>
      </TooltipShield>
    </Tooltip>
  );
}
